import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveImageValue } from "../../utils/imageOptions";

/**
 * Brand credibility strip — the part manufacturers we supply (Garrett, BorgWarner, Bosch, Delphi,
 * Siemens VDO, Mitsubishi, Denso…). Logos render grayscale → colour on hover; a brand without a logo
 * falls back to a styled name chip. Content: { title, subtitle, brands: [{ name, image, href }] }.
 *
 * With 6+ brands the strip becomes a pure-CSS infinite marquee (`.apa-marquee`): the pill list is
 * rendered twice (clone aria-hidden) and translated -50% on a 40s loop — hover pauses, the edges
 * fade via mask, and prefers-reduced-motion collapses it back to a static centered wrap.
 */
type Brand = { name?: string; image?: unknown; href?: { slug?: string } };

function BrandPill({ brand }: { brand: Brand }) {
  const { src } = resolveImageValue(brand.image as never);
  const inner = src ? (
    <RuntimeImage src={src} alt={brand.name ?? ""} className="max-h-10 w-auto max-w-[88px] object-contain opacity-90 saturate-[.8] transition duration-200 group-hover:scale-105 group-hover:opacity-100 group-hover:saturate-100 md:max-h-14 md:max-w-[118px]" />
  ) : (
    <span className="text-base font-extrabold tracking-tight text-[#6B7280] transition group-hover:text-[#121212] md:text-lg">{brand.name}</span>
  );
  const cls = "group flex h-20 min-w-[118px] shrink-0 items-center justify-center rounded-xl border border-[#C7D2CC] bg-[linear-gradient(145deg,#FFFFFF_0%,#F4F7F5_100%)] px-5 shadow-[0_5px_16px_rgba(18,38,28,.07)] transition duration-200 hover:border-[#00B862] md:h-24 md:min-w-[158px] md:px-8";
  return brand.href?.slug ? (
    <RuntimeLink href={brand.href} className={cls}>{inner}</RuntimeLink>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

export function BrandStripRenderer({
  component,
}: {
  component: { content?: { title?: string; subtitle?: string; brands?: Brand[] } };
}) {
  const { title, subtitle, brands = [] } = component.content ?? {};
  if (brands.length === 0) return null;
  const marquee = brands.length >= 6;

  return (
    <section className="py-6 md:py-8">
      {title ? (
        <div className="mb-5 text-center md:mb-6">
          <h2 className="text-xl font-bold tracking-tight text-[#0A0A0A] md:text-2xl" style={{ fontFamily: "var(--font-aleo)" }}>{title}</h2>
          {subtitle ? <p className="mt-1.5 text-sm text-[#6B7280]">{subtitle}</p> : null}
          <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-[#00B862]" aria-hidden="true" />
        </div>
      ) : null}
      {marquee ? (
        <div className="apa-marquee">
          <div className="apa-marquee-track">
            {/* Two byte-identical halves: translateX(-50%) lands exactly on the
                start of the clone, so the loop is seamless. */}
            <div className="flex shrink-0 gap-3 pr-3 md:gap-5 md:pr-5">
              {brands.map((b, i) => (
                <BrandPill key={i} brand={b} />
              ))}
            </div>
            <div aria-hidden="true" className="flex shrink-0 gap-3 pr-3 md:gap-5 md:pr-5">
              {brands.map((b, i) => (
                <BrandPill key={`clone-${i}`} brand={b} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5">
          {brands.map((b, i) => (
            <BrandPill key={i} brand={b} />
          ))}
        </div>
      )}
    </section>
  );
}
